/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Help from '@mui/icons-material/Help';
import NoAccounts from '@mui/icons-material/NoAccounts';
import PendingActions from '@mui/icons-material/PendingActions';
import Rsvp from '@mui/icons-material/Rsvp';
import Send from '@mui/icons-material/Send';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import { getScheduleResponsesForSchedule } from '../../../redux/schedule/actions';
import ScheduleService from '../../../services/ScheduleService';
import { blueGrey } from '../../../utils/brandColors';

const ParticipantListComponent = ({
  schedule,
  participants,
  occurrenceDate,
  defaultOpen,
  simplified,
}) => {
  const dispatch = useDispatch();
  const { mode } = useColorScheme();
  const colorMode = mode || 'light';

  const [responses, setResponses] = React.useState([]);
  const [openParticipantList, setOpenParticipantList] = React.useState(defaultOpen);
  const [open, setOpen] = React.useState(false);

  const {
    isFetchingScheduleResponses,
    scheduleResponses,
  } = useSelector((state) => state.schedule);

  useEffect(() => {
    setOpenParticipantList(defaultOpen);
  }, [defaultOpen]);

  useEffect(() => {
    if (schedule && schedule.publicId) {
      setResponses([]);
      dispatch(getScheduleResponsesForSchedule(schedule, occurrenceDate || null));
    }
  }, [schedule, occurrenceDate]);

  useEffect(() => {
    // map responses with user
    const updatedScheduleResponses = participants.map((u) => ({
      response: [...scheduleResponses]
        // put responses for specific occurrence date first
        .sort((a, b) => new Date(a.occurrenceDate
          || 3000000000000).getTime() - new Date(b.occurrenceDate || 3000000000000).getTime())
        .find((r) => (r.responderId === u.auth0UserId || r.responderId === u.email)),
      user: u,
    }));

    setResponses(updatedScheduleResponses);
  }, [scheduleResponses])

  const handleRequestResponse = (participant) => {
    ScheduleService.sendScheduleInviteRequest(schedule.publicId, participant.auth0UserId || participant.email).then(() => {
      // success
      setOpen(true);
    });
  };
  const listSurface = colorMode === 'dark'
    ? alpha('#ffffff', 0.04)
    : alpha('#f8fafc', 0.95);
  const listBorder = `1px solid ${alpha('#64748b', colorMode === 'dark'
    ? 0.22
    : 0.14)}`;
  const itemSurface = colorMode === 'dark'
    ? alpha('#ffffff', 0.03)
    : '#ffffff';
  const summaryChipSx = {
    borderRadius: 1,
    fontWeight: 700,
  };

  if (!isFetchingScheduleResponses && participants.length === 0) {
    return (
      <Box
        sx={{
          border: listBorder,
          borderRadius: 1.5,
          color: 'text.secondary',
          p: 1.5,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2">No participants</Typography>
      </Box>
    )
  }

  const getIconForResponseStatus = (status, user) => {
    if (!simplified && (!status || status === 'None')) {
      return (
        <Box>
          <Tooltip title="Resend Invite"><Send
            sx={{
              color: blueGrey[400],
              mr: 0.5,
              '&:hover': {
                cursor: 'pointer',
                color: blueGrey[200],
              },
            }}
            onClick={() => handleRequestResponse(user)}
          />
          </Tooltip>
          <Tooltip title="No Response"><Help sx={{ color: blueGrey[100] }} /></Tooltip>
        </Box>
      );
    }
    if (status === 'Accepted') {
      return <Tooltip title="Accepted"><Check color="success" /></Tooltip>;
    }
    if (status === 'Declined') {
      return <Tooltip title="Declined"><Close color="error" /></Tooltip>;
    }
    if (status === 'Tentative') {
      return <Tooltip title="Tentative"><PendingActions color="warning" /></Tooltip>;
    }
    return <Tooltip title="No Response"><Help sx={{ color: blueGrey[100] }} /></Tooltip>;
  }

  const getIconsForResponse = (user, response) => (
    <Box>
      {schedule && ((user && user.id) || !!response || simplified) && (getIconForResponseStatus(response && response.status, user))}
      {
        !simplified && !user.id && !response && (
          <Tooltip title="No Account - No invite responses can be recorded."><NoAccounts color="secondary" /></Tooltip>
        )
      }
    </Box>
  )

  const yesResponses = responses.filter((r) => r.response && r.response.status === 'Accepted').length;
  const noResponses = responses.filter((r) => r.response && r.response.status === 'Declined').length;
  const maybeResponses = responses.filter((r) => r.response && r.response.status === 'Tentative').length;
  const pendingResponses = responses.filter((r) => (r.response && r.response.status === 'None') || !r.response).length;
  const sortedResponses = [...responses].sort((a, b) => -b.user.userFullName.localeCompare(a.user.userFullName));

  return (
    <Box>
      <ListItemButton
        sx={{
          backgroundColor: listSurface,
          border: listBorder,
          borderRadius: 1.5,
          mb: openParticipantList
            ? 1
            : 0,
          px: 1.25,
          py: 1,
        }}
        onClick={() => setOpenParticipantList(!openParticipantList)}
      >
        <ListItemIcon
          sx={{
            minWidth: 34,
            mt: 0.25,
          }}
        ><Rsvp />
        </ListItemIcon>
        <ListItemText
          primary={(
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              Responses
            </Typography>
          )}
          secondary={(
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                mt: 0.75,
              }}
            >
              <Chip color="success" label={`${yesResponses} Yes`} size="small" sx={summaryChipSx} />
              <Chip color="error" label={`${noResponses} No`} size="small" sx={summaryChipSx} />
              <Chip color="warning" label={`${maybeResponses} Maybe`} size="small" sx={summaryChipSx} />
              <Chip label={`${pendingResponses} Pending`} size="small" sx={summaryChipSx} />
            </Box>
          )}
        />
        {(openParticipantList
          ? <ExpandLess sx={{ m: 1 }} onClick={(e) => { e.stopPropagation(); setOpenParticipantList(false); }} />
          : <ExpandMore sx={{ m: 1 }} onClick={(e) => { e.stopPropagation(); setOpenParticipantList(true); }} />)}
      </ListItemButton>
      <Collapse in={openParticipantList} timeout="auto" unmountOnExit>
        <List
          component="div"
          dense
          sx={{
            display: 'grid',
            gap: 0.75,
            maxHeight: 400,
            overflow: 'auto',
            p: 0,
          }}
        >
          {
            isFetchingScheduleResponses && [1, 2, 3].map((load) => (
              <ListItem
                key={`load-participant-${load}`}
                disablePadding
                component="div"
              >
                <ListItem>
                  <ListItemAvatar>
                    <Avatar />
                  </ListItemAvatar>
                  <Skeleton width={150} height={35} />
                </ListItem>
              </ListItem>
            ))
          }
          {
            !isFetchingScheduleResponses && (
              sortedResponses.map((r) => {
                const labelId = `participant-list-${r.user.auth0UserId || r.user.email || r.user.userFullName}-${(schedule && schedule.id) || '-1'}`;
                return (
                  <ListItem
                    key={labelId}
                    component="div"
                    sx={{
                      backgroundColor: itemSurface,
                      border: listBorder,
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.75,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        alt={r.user.userFullName}
                        src={r.user.avatar}
                        sx={{
                          width: 32,
                          height: 32,
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      id={labelId}
                      primary={r.user.userFullName}
                      primaryTypographyProps={{
                        fontWeight: 700,
                        noWrap: false,
                      }}
                      secondary={`${(schedule && (r.user.email === schedule.createdByEmail || r.user.userFullName === schedule.createdByName) && '(organizer) ') || ''}${(r.response && r.response.note && `"${r.response.note}"`) || ''}`}
                      secondaryTypographyProps={{ noWrap: false }}
                    />
                    {
                      !(schedule && (r.user.email === schedule.createdByEmail || r.user.userFullName === schedule.createdByName))
                      && getIconsForResponse(r.user, r.response)
                    }
                  </ListItem>
                );
              })
            )
          }
        </List>
      </Collapse>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
      >
        <Alert onClose={() => setOpen(false)} severity="success" sx={{ width: '100%' }}>
          Invite successfully sent!
        </Alert>
      </Snackbar>
    </Box>
  );
};

ParticipantListComponent.propTypes = {
  schedule: PropTypes.object,
  participants: PropTypes.array.isRequired,
  occurrenceDate: PropTypes.any,
  defaultOpen: PropTypes.bool,
  simplified: PropTypes.bool,
};

ParticipantListComponent.defaultProps = {
  schedule: null,
  occurrenceDate: null,
  defaultOpen: false,
  simplified: false,
};

export default (ParticipantListComponent);
